import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useExecutionStore } from '../../lib/storage/provider';
import type { StoredCaptureEvent } from '../../lib/types';

import { ExecutionInspector } from './execution-inspector';

export function ExecutionInspectorClient({
  projectId,
  executionId,
  agentUrl,
}: {
  projectId: string;
  executionId: string;
  agentUrl: string | null;
}): import('react/jsx-runtime').JSX.Element {
  const { getDetails, isLoading: storeLoading } = useExecutionStore();
  const [events, setEvents] = useState<StoredCaptureEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect((): (() => void) | void => {
    if (!storeLoading) return;
    const t = setTimeout(() => {
      setError('Timed out initializing execution store');
      setLoading(false);
    }, 60_000);
    return () => clearTimeout(t);
  }, [storeLoading]);

  useEffect((): void => {
    if (storeLoading) return;

    async function load(): Promise<void> {
      let settled = false;
      const watchdog = setTimeout(() => {
        if (settled) return;
        setError('Timed out loading execution details');
        setLoading(false);
      }, 60_000);

      try {
        const data = await getDetails(executionId, projectId);
        settled = true;
        setEvents(data as StoredCaptureEvent[]);
      } catch (err: unknown) {
        settled = true;
        console.error('[Inspector] Failed to load details:', err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
      }
    }
    void load();
  }, [executionId, getDetails, storeLoading]);

  if ((loading || storeLoading) && error == null) {
    return (
      <div
        className="flex flex-col items-center justify-center h-[calc(100vh-200px)] gap-3 text-muted-foreground"
        data-testid="inspector-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[var(--flux-accent)]" />
        <p className="text-sm font-mono animate-pulse">Loading execution trace...</p>
      </div>
    );
  }

  if (error != null || !events) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground p-8"
        data-testid="inspector-error"
      >
        <p className="text-sm">Failed to load execution details: {error ?? 'Not found'}</p>
      </div>
    );
  }

  return (
    <div data-testid="inspector-ready">
      <ExecutionInspector
        executionId={executionId}
        projectId={projectId}
        events={events}
        agentUrl={agentUrl}
      />
    </div>
  );
}
